import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'
import { Grid, makeStyles, Paper, styled } from '@material-ui/core'
import { Link } from 'react-router-dom'
import { Typography } from 'src/modules/ui'
import RecipeActions from './RecipeActions'
import { generateExtraChips, mapRecipeChipKeyToLabel } from '../utils'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  boxShadow: 'none',
}))

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    color: 'inherit',
    display: 'flex',
    flexDirection: 'row',
    maxWidth: 900,
    padding: theme.spacing(2, 2, 1),
    textDecoration: 'inherit',
    '&:hover': {
      backgroundColor: theme.palette.fadedGreen.light,
      cursor: 'pointer',
    },
  },
  actions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  details: {
    alignItems: 'center',
    color: theme.palette.darkGreen.main,
    display: 'flex',
    flexDirection: 'row',
  },
  body: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },

  detailsLogoContainer: {
    display: 'flex',
  },
  dietaryInfocontainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  description: {
    marginTop: theme.spacing(1.5),
    flexGrow: 1,
    display: 'box',
    lineClamp: 4,
    maxHeight: 96,
    boxOrient: 'vertical',
    overflow: 'hidden',
  },
  logo: {
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    display: 'none',
    flexShrink: 0,
    height: 120,
    justifyContent: 'center',
    marginRight: theme.spacing(4),
    objectFit: 'cover',
    textAlign: 'center',
    width: 120,
    [theme.breakpoints.up('mobile')]: {
      display: 'flex',
    },
  },
  logoMobile: {
    display: 'block',
    height: 70,
    marginRight: theme.spacing(1),
    objectFit: 'cover',
    width: 70,
    [theme.breakpoints.up('mobile')]: {
      display: 'none',
    },
  },
  name: {
    color: theme.palette.orange.main,
    fontSize: '1.25rem',
    fontFamily: 'Gotham',
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      lineHeight: 1.5,
    },
  },
  infoName: {
    fontSize: '0.7rem',
  },
  infoValue: {
    color: theme.palette.orange.main,
    fontSize: '0.7rem',
  },
}))

const RecipeCard = ({
  canMessage,
  canRequestAgreement,
  canStar,
  className,
  linkPrefix,
  onClick,
  purchasingEnvironment,
  ...recipe
}) => {
  const { id, name, photo, nutritionalInfo, allergens } = recipe
  const classes = useStyles()

  const calories = nutritionalInfo?.Energy?.value ?? null
  const { Energy, ...remainingChips } = nutritionalInfo || {}

  // const dietaryInfo = [
  //   ...(allergens && allergens.contains ? allergens.contains : []),
  //   ...(allergens && allergens.may_contain ? allergens.may_contain : []),
  // ].join(', ')

  const contains = allergens && allergens.contains ? allergens.contains : []
  const mayContain =
    allergens && allergens.mayContain ? allergens.mayContain : []

  const hasAllergens = contains.length > 0 || mayContain.length > 0

  const extraChips = { ...remainingChips, ...generateExtraChips(recipe) }

  return (
    <Link
      className={clsx(classes.root, className)}
      onClick={onClick}
      to={`${linkPrefix}/recipes/detail/${id}`}
    >
      {photo ? (
        <img className={classes.logo} src={photo} alt={name} />
      ) : (
        <div className={classes.logo}>
          <Typography variant="body2" color="lightGreen">
            {name}
          </Typography>
        </div>
      )}
      <div className={classes.body}>
        <div className={classes.details}>
          <div className={classes.detailsLogoContainer}>
            <img className={classes.logoMobile} src={photo} alt={name} />
            <div>
              <Typography className={classes.name}>{name}</Typography>
              <Typography className={classes.details} variant="body2">
                {calories ? `${calories} kcal` : ''}
              </Typography>
            </div>
          </div>
        </div>
        {extraChips ? (
          <div className={classes.dietaryInfocontainer}>
            <Grid container spacing={1}>
              {Object.keys(extraChips).map(key => {
                return (
                  <Grid item style={{ width: '75px' }}>
                    <Item>
                      <Typography className={classes.infoName}>
                        {mapRecipeChipKeyToLabel(key)}
                      </Typography>
                      <Typography className={classes.infoValue} noWrap>
                        {extraChips[key].value} {extraChips[key].unit}
                      </Typography>
                    </Item>
                  </Grid>
                )
              })}
            </Grid>
          </div>
        ) : null}

        {hasAllergens ? (
          <Grid item xs={12}>
            {contains.length > 0 ? (
              <Typography className={classes.infoName} noWrap>
                Contains: {contains.join(', ')}
              </Typography>
            ) : null}
            {mayContain.length > 0 ? (
              <Typography className={classes.infoName} noWrap>
                May Contain: {mayContain.join(', ')}
              </Typography>
            ) : null}
          </Grid>
        ) : null}

        <RecipeActions
          className={classes.actions}
          purchasingEnvironment={purchasingEnvironment}
          {...recipe}
        />
      </div>
    </Link>
  )
}

RecipeCard.propTypes = {
  address: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    county: PropTypes.string,
    postcode: PropTypes.string,
  }),
  canStar: PropTypes.bool,
  canMessage: PropTypes.bool,
  canRequestAgreement: PropTypes.bool,
  description: PropTypes.string,
  id: PropTypes.number,
  logo: PropTypes.string,
  // This is used to prefix public supplier URLs with /public
  linkPrefix: PropTypes.string,
  matchingProducts: PropTypes.number,
  proposalStatus: PropTypes.oneOf(['requested', 'proposed', 'accepted']),
  reviewCount: PropTypes.number,
  starred: PropTypes.bool,
  totalProducts: PropTypes.number,
}

RecipeCard.defaultProps = {
  canStar: true,
  canMessage: true,
  canRequestAgreement: true,
  linkPrefix: '',
}

export default RecipeCard
